import { ChannelSubscription, type IChannelSubscription } from './ChannelSubscription'
import type { ChannelOptions } from './types'
import { WebSocketConnection } from './WebSocketConnection'

/**
 * Channel manager for handling multiple WebSocket channel subscriptions
 * Provides a centralized way to manage connections and subscriptions
 */
export class ChannelManager {
  private static instance: ChannelManager | null = null
  private connections: Map<string, WebSocketConnection> = new Map()
  private subscriptions: Map<string, ChannelSubscription> = new Map()
  private defaultOptions: ChannelOptions = {
    autoReconnect: true,
    maxReconnectAttempts: 5,
    reconnectDelay: 1000
  }

  /**
   * Get singleton instance of ChannelManager
   */
  static getInstance(): ChannelManager {
    if (!ChannelManager.instance) {
      ChannelManager.instance = new ChannelManager()
    }
    return ChannelManager.instance
  }

  /**
   * Subscribe to a channel
   */
  static subscribe(channelName: string, options?: ChannelOptions): IChannelSubscription {
    return ChannelManager.getInstance().subscribeToChannel(channelName, options)
  }

  /**
   * Unsubscribe from a channel
   */
  static unsubscribe(channelName: string): void {
    ChannelManager.getInstance().unsubscribeFromChannel(channelName)
  }

  /**
   * Get existing connection for a channel
   */
  static getConnection(channelName: string): WebSocketConnection | null {
    return ChannelManager.getInstance().getConnectionForChannel(channelName)
  }

  /**
   * Get existing subscription for a channel
   */
  static getSubscription(channelName: string): IChannelSubscription | null {
    return ChannelManager.getInstance().getSubscriptionForChannel(channelName)
  }

  /**
   * Check if a channel is currently subscribed
   */
  static isSubscribed(channelName: string): boolean {
    return ChannelManager.getInstance().isChannelSubscribed(channelName)
  }

  /**
   * Get all active channel names
   */
  static getActiveChannels(): string[] {
    return ChannelManager.getInstance().getActiveChannelNames()
  }

  /**
   * Cleanup all connections and subscriptions
   */
  static cleanup(): void {
    ChannelManager.getInstance().cleanupAll()
  }

  /**
   * Set default options for new connections
   */
  static setDefaultOptions(options: Partial<ChannelOptions>): void {
    ChannelManager.getInstance().updateDefaultOptions(options)
  }

  /**
   * Private constructor for singleton pattern
   */
  private constructor() {}

  /**
   * Subscribe to a channel (instance method)
   */
  private subscribeToChannel(channelName: string, options?: ChannelOptions): IChannelSubscription {
    // Check if subscription already exists
    const existingSubscription = this.subscriptions.get(channelName)
    if (existingSubscription) {
      return existingSubscription
    }

    // Get or create connection for this channel
    const connection = this.getOrCreateConnection(channelName, options)

    // Create new subscription
    const subscription = new ChannelSubscription(channelName, connection)
    this.subscriptions.set(channelName, subscription)

    return subscription
  }

  /**
   * Unsubscribe from a channel (instance method)
   */
  private unsubscribeFromChannel(channelName: string): void {
    const subscription = this.subscriptions.get(channelName)
    if (subscription) {
      subscription.unsubscribe()
      this.subscriptions.delete(channelName)
    }

    // Check if connection is still needed by other subscriptions
    this.cleanupConnectionIfUnused(channelName)
  }

  /**
   * Get connection for a channel (instance method)
   */
  private getConnectionForChannel(channelName: string): WebSocketConnection | null {
    return this.connections.get(channelName) || null
  }

  /**
   * Get subscription for a channel (instance method)
   */
  private getSubscriptionForChannel(channelName: string): IChannelSubscription | null {
    return this.subscriptions.get(channelName) || null
  }

  /**
   * Check if channel is subscribed (instance method)
   */
  private isChannelSubscribed(channelName: string): boolean {
    const subscription = this.subscriptions.get(channelName)
    return subscription?.isSubscribed() ?? false
  }

  /**
   * Get all active channel names (instance method)
   */
  private getActiveChannelNames(): string[] {
    return Array.from(this.subscriptions.keys()).filter((channelName) =>
      this.subscriptions.get(channelName)?.isSubscribed()
    )
  }

  /**
   * Update default options (instance method)
   */
  private updateDefaultOptions(options: Partial<ChannelOptions>): void {
    this.defaultOptions = { ...this.defaultOptions, ...options }
  }

  /**
   * Get or create WebSocket connection for a channel
   */
  private getOrCreateConnection(
    channelName: string,
    options?: ChannelOptions
  ): WebSocketConnection {
    const existingConnection = this.connections.get(channelName)
    if (existingConnection) {
      return existingConnection
    }

    // Build WebSocket URL for the channel
    const wsUrl = this.buildWebSocketUrl(channelName)

    // Merge options with defaults
    const connectionOptions: ChannelOptions = {
      ...this.defaultOptions,
      ...options
    }

    // Create new connection
    const connection = new WebSocketConnection(wsUrl, connectionOptions)
    this.connections.set(channelName, connection)

    return connection
  }

  /**
   * Build WebSocket URL for a specific channel
   */
  private buildWebSocketUrl(channelName: string): string {
    // For case channels, extract case UUID and build appropriate URL
    if (channelName.startsWith('case_')) {
      const caseUuid = channelName.replace('case_', '')
      return `/ws/cases/${caseUuid}/`
    }

    // For organization document channels
    if (channelName.startsWith('org_documents_')) {
      const orgUuid = channelName.replace('org_documents_', '')
      return `/ws/organization/${orgUuid}/documents/`
    }

    // For other channel types, we could add more URL patterns here
    // For now, use a generic pattern
    return `/ws/channels/${channelName}/`
  }

  /**
   * Cleanup connection if no longer used by any subscriptions
   */
  private cleanupConnectionIfUnused(channelName: string): void {
    const connection = this.connections.get(channelName)
    if (!connection) {
      return
    }

    // Check if any other subscriptions are using this connection
    const hasActiveSubscriptions = Array.from(this.subscriptions.values()).some(
      (subscription) => subscription.getConnection() === connection && subscription.isSubscribed()
    )

    if (!hasActiveSubscriptions) {
      connection.disconnect()
      this.connections.delete(channelName)
    }
  }

  /**
   * Cleanup all connections and subscriptions
   */
  private cleanupAll(): void {
    // Unsubscribe from all channels
    Array.from(this.subscriptions.keys()).forEach((channelName) => {
      this.unsubscribeFromChannel(channelName)
    })

    // Disconnect all remaining connections
    this.connections.forEach((connection) => {
      connection.destroy()
    })

    this.connections.clear()
    this.subscriptions.clear()
  }
}

// Export singleton instance methods as default export for convenience
export default ChannelManager
